import {
  Box,
  Button,
  Chip,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import { resolveLandingAssetUrl } from "../../../utils/landingAssets";
import {
  normalizeImagePosition,
  normalizeVisualStyle,
  segmentCssVariables,
  segmentVisualScale,
  visualStyleFromTemplate,
} from "../landingStudio/segmentVisualStyle";
import { youtubeEmbedUrl } from "./youtube";
import { normalizeCalendlyUrl } from "../../../utils/calendly";
import { applyLandingSegmentSnapshot, getLandingSegments } from "./landingLayout";
import {
  getLandingPreviewDevice,
  resolveLandingPreviewResponsiveValue,
} from "../landingStudio/previewDevices";
import {
  HeroCanvasCtaIcon,
  HighlightCanvasIcon,
  SafeCanvasImage,
  mergeDesignTokens,
  finite,
  px,
  normalizeHeroHorizontalPosition,
  normalizeHeroVerticalPosition,
  heroHorizontalFlex,
  heroVerticalFlex,
  colorWithAlpha,
  SectionFrame,
  PlaceholderCards,
} from "./canvas/LandingCanvasPrimitives";


function configuredStyleValue(style, key) {
  return style && Object.prototype.hasOwnProperty.call(style, key) && style[key] !== "" && style[key] !== null && style[key] !== undefined;
}

function optionalBoundedNumber(value, min, max) {
  if (value === "" || value === null || value === undefined) return null;
  const number = Number(value);
  if (!Number.isFinite(number)) return null;
  return Math.min(max, Math.max(min, number));
}

function cssColorWithOpacity(color, opacity = 1) {
  const candidate = String(color || "").trim();
  const alpha = Math.max(0, Math.min(1, Number.isFinite(Number(opacity)) ? Number(opacity) : 1));
  if (!candidate || candidate.toLowerCase() === "transparent" || alpha <= 0) return "transparent";
  if (alpha >= 0.999) return candidate;
  const match = candidate.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return candidate;
  const raw = match[1].length === 3
    ? match[1].split("").map((char) => `${char}${char}`).join("")
    : match[1];
  const red = Number.parseInt(raw.slice(0, 2), 16);
  const green = Number.parseInt(raw.slice(2, 4), 16);
  const blue = Number.parseInt(raw.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
}

function heroCtaCanvasSx(style, { outlined, colors, fallbackShadow }) {
  const current = style && typeof style === "object" ? style : {};
  const hasCustom = Object.keys(current).some((key) => configuredStyleValue(current, key));
  if (!hasCustom) {
    return outlined
      ? { color: colors.onSecondary, borderColor: colors.onSecondary, textTransform: "none" }
      : { bgcolor: colors.primary, color: colors.onPrimary, textTransform: "none", boxShadow: fallbackShadow };
  }
  const background = String(current.backgroundColor || "").trim() || (outlined ? "#ffffff" : colors.primary);
  const hoverBackground = String(current.hoverBackgroundColor || "").trim() || (outlined ? "#ffffff" : colors.secondary);
  const textColor = String(current.textColor || "").trim() || (outlined ? colors.onSecondary : colors.onPrimary);
  const hoverTextColor = String(current.hoverTextColor || "").trim() || textColor;
  const borderColor = String(current.borderColor || "").trim() || colors.onSecondary;
  const borderWidth = configuredStyleValue(current, "borderWidth") ? Number(current.borderWidth) : (outlined ? 1 : 0);
  const shadowOpacity = configuredStyleValue(current, "shadowOpacity") ? Number(current.shadowOpacity) : (outlined ? 0 : 0.28);
  const shadowColor = String(current.shadowColor || "").trim() || "#000000";
  const shadow = shadowOpacity <= 0
    ? "none"
    : `${configuredStyleValue(current, "shadowX") ? Number(current.shadowX) : 0}px ${configuredStyleValue(current, "shadowY") ? Number(current.shadowY) : (outlined ? 0 : 10)}px ${configuredStyleValue(current, "shadowBlur") ? Number(current.shadowBlur) : (outlined ? 0 : 28)}px ${configuredStyleValue(current, "shadowSpread") ? Number(current.shadowSpread) : 0}px ${cssColorWithOpacity(shadowColor, shadowOpacity)}`;
  const sx = {
    textTransform: "none",
    color: textColor,
    bgcolor: cssColorWithOpacity(background, configuredStyleValue(current, "backgroundOpacity") ? current.backgroundOpacity : (outlined ? 0.08 : 1)),
    borderStyle: borderWidth > 0 ? "solid" : "none",
    borderWidth: `${borderWidth}px`,
    borderColor,
    boxShadow: shadow,
    "&:hover": {
      color: hoverTextColor,
      bgcolor: cssColorWithOpacity(hoverBackground, configuredStyleValue(current, "hoverBackgroundOpacity") ? current.hoverBackgroundOpacity : (outlined ? 0.18 : 1)),
      borderColor,
    },
  };
  if (configuredStyleValue(current, "borderRadius")) sx.borderRadius = `${Number(current.borderRadius)}px`;
  if (configuredStyleValue(current, "fontSize")) sx.fontSize = `${Number(current.fontSize)}px`;
  if (configuredStyleValue(current, "fontWeight")) sx.fontWeight = current.fontWeight;
  if (configuredStyleValue(current, "minWidth")) sx.minWidth = `${Number(current.minWidth)}px`;
  if (configuredStyleValue(current, "minHeight")) sx.minHeight = `${Number(current.minHeight)}px`;
  if (configuredStyleValue(current, "paddingX")) sx.px = `${Number(current.paddingX)}px`;
  if (configuredStyleValue(current, "paddingY")) sx.py = `${Number(current.paddingY)}px`;
  return sx;
}

export default function LandingCanvas({
  form = {},
  activeSection = "",
  selectedElement = null,
  device = "desktop",
  productsEnabled = true,
  onSelectSection,
  onSelectElement,
  studioMode = false,
  onOpenSegmentTool,
  onToggleSection,
  onlySection = "",
  bare = false,
  layoutInstanceId = "",
}) {
  const business = form.business || {};
  const navigation = form.navigation || {};
  const hero = form.hero || {};
  const catalog = form.catalog || {};
  const catalogCards = catalog.cards || {};
  const about = form.about || {};
  const highlightsIntro = form.highlightsIntro || {};
  const highlights = Array.isArray(form.highlights) ? form.highlights : [];
  const contact = form.contact || {};
  const custom = form.custom || {};
  const footer = form.footer || {};
  const sections = form.sections || {};
  const theme = form.theme || {};
  const layoutSegments = getLandingSegments(form);
  const segmentAllowed = (type) => onlySection
    ? onlySection === type
    : layoutSegments.some((segment) => segment.id === type);
  const orderFor = (type) => {
    const targetId = layoutInstanceId || type;
    const index = layoutSegments.findIndex((segment) => segment.id === targetId);
    return index >= 0 ? index : layoutSegments.length;
  };
  const sectionBackgroundSx = (type) => {
    const targetId = layoutInstanceId || type;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === type);
    const imageUrl = resolveLandingAssetUrl(segment?.visualStyle?.backgroundImageUrl);
    if (!imageUrl) return {};
    return {
      backgroundImage: `url("${String(imageUrl).replace(/"/g, "%22")}")`,
      backgroundSize: "cover",
      backgroundPosition: normalizeImagePosition(segment?.visualStyle?.backgroundImagePosition),
      backgroundRepeat: "no-repeat",
    };
  };
  const tokens = mergeDesignTokens(theme, form.designTokens);
  const colors = tokens.colors;
  const paletteForSection = (sectionKey) => {
    const targetId = sectionKey === "custom" && layoutInstanceId ? layoutInstanceId : sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    return segment?.visualStyle?.useGlobalColors === false
      ? { ...colors, ...(segment.visualStyle.colors || {}) }
      : colors;
  };
  const elementColorSxForSection = (sectionKey) => {
    const targetId = sectionKey === "custom" && layoutInstanceId ? layoutInstanceId : sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    const elementColors = segment?.visualStyle?.elementColors;
    if (!elementColors || typeof elementColors !== "object") return {};
    return Object.entries(elementColors).reduce((sx, [path, rawStyle]) => {
      if (!path || !rawStyle || typeof rawStyle !== "object") return sx;
      const safePath = String(path).replace(/["\\]/g, "\\$&");
      const selector = `& [data-landing-field="${safePath}"], & [data-landing-color-field="${safePath}"]`;
      const normal = {};
      const hover = {};
      if (rawStyle.color) normal.color = `${rawStyle.color} !important`;
      if (rawStyle.background) normal.backgroundColor = `${rawStyle.background} !important`;
      if (rawStyle.borderColor) normal.borderColor = `${rawStyle.borderColor} !important`;
      if (rawStyle.shadow) {
        normal.boxShadow = `0 8px 24px ${rawStyle.shadow} !important`;
        normal.textShadow = `0 2px 10px ${rawStyle.shadow} !important`;
      }
      if (rawStyle.hoverColor) hover.color = `${rawStyle.hoverColor} !important`;
      if (rawStyle.hoverBackground) hover.backgroundColor = `${rawStyle.hoverBackground} !important`;
      if (Object.keys(normal).length) sx[selector] = normal;
      if (Object.keys(hover).length) sx[`${selector}:hover`] = hover;
      return sx;
    }, {});
  };
  const navbarColors = paletteForSection("navbar");
  const heroColors = paletteForSection("hero");
  const spotlightColors = paletteForSection("spotlight");
  const featuredColors = paletteForSection("featured");
  const brandsColors = paletteForSection("brands");
  const categoriesColors = paletteForSection("categories");
  const aboutColors = paletteForSection("about");
  const highlightsColors = paletteForSection("highlights");
  const customColors = paletteForSection("custom");
  const contactColors = paletteForSection("contact");
  const footerColors = paletteForSection("footer");
  const typography = tokens.typography;
  const spacing = tokens.spacing;
  const borders = tokens.borders;
  const shadows = tokens.shadows;
  const previewDevice = getLandingPreviewDevice(device);
  const responsive = (values, fallback) =>
    resolveLandingPreviewResponsiveValue(previewDevice.id, values, fallback);
  const desktopLayout = previewDevice.id === "desktop";
  const mobileLayout = previewDevice.id === "mobile";

  const heroHorizontalPosition = normalizeHeroHorizontalPosition(
    hero.contentHorizontalAlign ??
      hero.contentHorizontalPosition ??
      hero.textHorizontalPosition ??
      hero.contentPositionHorizontal ??
      hero.horizontalPosition
  );
  const heroVerticalPosition = normalizeHeroVerticalPosition(
    hero.contentVerticalAlign ??
      hero.contentVerticalPosition ??
      hero.textVerticalPosition ??
      hero.contentPositionVertical ??
      hero.verticalPosition
  );
  const heroContentJustify = heroHorizontalFlex(heroHorizontalPosition);
  const heroContentAlign = heroVerticalFlex(heroVerticalPosition);
  const heroTextAlign = heroHorizontalPosition;

  const primary = colors.primary;
  const secondary = colors.secondary;
  const surface = colors.surface;
  const background = colors.background;
  const text = colors.text;
  const muted = colors.mutedText;
  const onPrimary = colors.onPrimary;
  const onSecondary = colors.onSecondary;
  const horizontalPadding = responsive(
    { xs: px(spacing.md, 16), md: px(spacing.xxl, 48) },
    px(spacing.md, 16)
  );
  const cardBorder = `${finite(borders.width, 1)}px ${borders.style || "solid"} ${borders.color || colors.divider}`;
  const sectionAppearance = (sectionKey, current, palette) => {
    const targetId = sectionKey === "custom" && layoutInstanceId ? layoutInstanceId : sectionKey;
    const segment = layoutSegments.find((candidate) => candidate.id === targetId)
      || layoutSegments.find((candidate) => candidate.id === sectionKey);
    if (segment?.visualStyle?.useGlobalColors !== false) return current || {};
    if (sectionKey === "about") {
      return {
        ...(current || {}),
        backgroundColor: current?.backgroundColor || palette.background,
        surfaceColor: current?.surfaceColor || palette.surface,
        accentColor: current?.accentColor || palette.primary,
        titleColor: current?.titleColor || palette.secondary,
        textColor: current?.textColor || palette.mutedText || palette.text,
      };
    }
    return {
      ...(current || {}),
      backgroundColor: palette.background,
      surfaceColor: palette.surface,
      accentColor: palette.primary,
      titleColor: palette.secondary,
      textColor: palette.mutedText || palette.text,
    };
  };
  const aboutAppearance = sectionAppearance("about", form.appearance?.about, aboutColors);
  const highlightsAppearance = sectionAppearance("highlights", form.appearance?.highlights, highlightsColors);
  const contactAppearance = sectionAppearance("contact", form.appearance?.contact, contactColors);
  const heroSlideIndex = Array.isArray(hero.slides)
    ? hero.slides.findIndex((slide) => slide?.url)
    : -1;
  const heroSlide = heroSlideIndex >= 0 ? hero.slides[heroSlideIndex] : null;
  const heroImageSourcePath = heroSlide
    ? `hero.slides.${heroSlideIndex}.url`
    : "hero.imageUrl";
  const heroImage = resolveLandingAssetUrl(heroSlide?.url || hero.imageUrl);
  const heroTitle = heroSlide?.title ?? hero.title ?? business.name ?? "";
  const heroSubtitle = heroSlide?.subtitle ?? hero.subtitle ?? "";
  const heroImagePosition = normalizeImagePosition(
    heroSlide?.position,
    normalizeImagePosition(hero.imagePosition)
  );
  const heroTitleFieldPath = heroSlide ? `hero.slides.${heroSlideIndex}.title` : "hero.title";
  const heroSubtitleFieldPath = heroSlide ? `hero.slides.${heroSlideIndex}.subtitle` : "hero.subtitle";
  const heroRotatingPhraseIndex = Array.isArray(hero.rotatingPhrases)
    ? hero.rotatingPhrases.findIndex((phrase) => String(phrase || "").trim())
    : -1;
  const heroRotatingPhrase = heroRotatingPhraseIndex >= 0 ? String(hero.rotatingPhrases[heroRotatingPhraseIndex] || "") : "";
  const heroSlideCount = Array.isArray(hero.slides) ? hero.slides.length : 0;
  const heroPhraseCount = Array.isArray(hero.rotatingPhrases) ? hero.rotatingPhrases.length : 0;
  const showHeroPrimaryCta = Boolean(hero.primaryCtaLabel && hero.primaryCtaUrl) &&
    (!hero.primaryCtaRequiresProducts || productsEnabled);
  const showHeroSecondaryCta = Boolean(hero.secondaryCtaLabel && hero.secondaryCtaUrl) &&
    (!hero.secondaryCtaRequiresProducts || productsEnabled);
  const showAboutCta = Boolean(about.ctaLabel && about.ctaUrl) &&
    (!about.ctaRequiresProducts || productsEnabled);
  const configuredBrands = Array.isArray(catalog.brands?.items)
    ? catalog.brands.items
        .map((item, sourceIndex) => ({ item, sourceIndex }))
        .filter(({ item }) => (item?.name ?? item?.label) || item?.logoUrl)
    : [];
  const logoSourcePath = theme.logoUrl ? "theme.logoUrl" : "theme.compactLogoUrl";
  const logo = resolveLandingAssetUrl(theme.logoUrl || theme.compactLogoUrl);
  const navbarLogo = navigation.showLogo !== false
    ? resolveLandingAssetUrl(
        desktopLayout
          ? theme.logoUrl
          : (theme.compactLogoUrl || theme.logoUrl)
      )
    : "";
  const navbarLogoColor = desktopLayout
    ? String(navigation.logoColor || "").trim()
    : String(navigation.mobile?.logoColor || "").trim();
  const navbarLogoMaxHeight = desktopLayout
    ? optionalBoundedNumber(navigation.logoMaxHeight, 20, 120)
    : optionalBoundedNumber(navigation.mobile?.logoMaxHeight, 20, 96);
  const footerLogoColor = String(footer.logoColor || "").trim();
  const footerLogoMaxHeight = optionalBoundedNumber(footer.logoMaxHeight, 20, 160);
  const aboutImage = resolveLandingAssetUrl(about.imageUrl);
  const highlightsBackground = resolveLandingAssetUrl(highlightsAppearance.backgroundImageUrl);
  const highlightsBackgroundPosition = normalizeImagePosition(highlightsAppearance.backgroundImagePosition);
  const highlightsBackgroundOpacity = Math.max(0, Math.min(1, finite(highlightsAppearance.backgroundImageOpacity, 0.24)));
  const contactImage = resolveLandingAssetUrl(contact.backgroundImageUrl);
  const contactImagePosition = normalizeImagePosition(contact.backgroundImagePosition);
  const spotlightLogo = resolveLandingAssetUrl(catalog.spotlight?.logoUrl);
  const spotlightBackground = resolveLandingAssetUrl(catalog.spotlight?.backgroundImageUrl);
  const spotlightBackgroundPosition = normalizeImagePosition(catalog.spotlight?.backgroundImagePosition);
  const footerCopyright = String(footer.copyright ?? "")
    .replace(/\{businessName\}/g, business.name ?? "")
    .replace(/\{year\}/g, String(new Date().getFullYear()));
  const navbarBusinessInfo = business.phone || business.email || business.address || "";
  const navbarButtons = Array.isArray(navigation.buttons)
    ? navigation.buttons
        .filter((item) => String(item?.label || "").trim())
        .slice(0, 8)
    : [];
  const heroHasContent = Boolean(
    heroImage ||
    hero.eyebrow ||
    heroTitle ||
    heroSubtitle ||
    (Array.isArray(hero.rotatingPhrases) && hero.rotatingPhrases.some(Boolean)) ||
    showHeroPrimaryCta ||
    showHeroSecondaryCta
  );
  const showSpotlightCta = Boolean(
    catalog.spotlight?.ctaLabel &&
    catalog.spotlight?.ctaUrl &&
    (!catalog.spotlight?.ctaRequiresProducts || productsEnabled)
  );
  const spotlightHasContent = Boolean(
    spotlightLogo ||
    catalog.spotlight?.eyebrow ||
    catalog.spotlight?.title ||
    catalog.spotlight?.description ||
    showSpotlightCta
  );
  const aboutHasCopy = Boolean(about.eyebrow || about.title || about.description || showAboutCta);
  const aboutHasContent = Boolean(aboutImage || aboutHasCopy);
  const visibleHighlights = highlights
    .map((item, index) => ({ item, index }))
    .filter(({ item }) => item?.title || item?.description || item?.imageUrl || item?.youtubeUrl);
  const highlightsHasContent = Boolean(
    highlightsIntro.eyebrow ||
    highlightsIntro.title ||
    highlightsIntro.subtitle ||
    visibleHighlights.length
  );
  const businessAddress = [business.address, business.city, business.province, business.country]
    .map((item) => String(item || "").trim())
    .filter(Boolean)
    .join(", ");
  const whatsappDigits = String(business.whatsapp || "").replace(/\D+/g, "");
  const contactHasDetails = Boolean(
    businessAddress || business.schedule || business.phone || business.email
  );
  const contactSocialCount = [
    business.socials?.instagram,
    business.socials?.facebook,
    business.socials?.linkedin,
    business.socials?.website,
  ].filter((value) => String(value || "").trim()).length;
  const contactHasActions = Boolean(
    (whatsappDigits && contact.whatsappCtaLabel) ||
    (contact.detailsCtaLabel && contact.detailsCtaUrl) ||
    contactHasDetails
  );
  const contactHasContent = Boolean(
    contact.title ||
    contact.subtitle ||
    contactHasActions ||
    contactImage ||
    business.mapEmbedUrl ||
    contactSocialCount
  );
  const footerBrandTagline = footer.tagline ?? business.shortDescription ?? "";
  const footerHomeLabel = footer.homeLabel ?? navigation.homeLabel ?? "Inicio";
  const footerProductsLabel = footer.productsLabel ?? navigation.productsLabel ?? "Productos";
  const footerContactLabel = footer.contactLabel ?? navigation.contactLabel ?? "Contacto";
  const footerHasBrand = Boolean((footer.showLogo !== false && logo) || business.name || footerBrandTagline);
  const footerSectionLinkVisible = (url, hasTarget) => {
    const target = String(url || "").trim();
    return !target.startsWith("#") || hasTarget;
  };
  const footerAboutVisible =
    sections.showAbout !== false &&
    navigation.showAboutLink !== false &&
    footerSectionLinkVisible(navigation.aboutUrl || "#about", aboutHasContent);
  const footerHighlightsVisible =
    sections.showHighlights !== false &&
    navigation.showHighlightsLink !== false &&
    footerSectionLinkVisible(navigation.highlightsUrl || "#highlights", highlightsHasContent);
  const footerContactVisible =
    sections.showContact !== false &&
    navigation.showContactLink !== false &&
    footerSectionLinkVisible(navigation.contactUrl || "/contact", contactHasContent);
  const footerHasNavigation = Boolean(
    footer.navigationTitle ||
    footerHomeLabel ||
    (productsEnabled && navigation.showProductsLink !== false && footerProductsLabel) ||
    (footerAboutVisible && navigation.aboutLabel) ||
    (footerHighlightsVisible && navigation.highlightsLabel) ||
    (footerContactVisible && footerContactLabel)
  );
  const footerHasContact = Boolean(
    businessAddress || business.schedule || business.email || business.phone
  );
  const footerSocialLabels = navigation.showSocialLinks === false
    ? []
    : [
        whatsappDigits ? "WhatsApp" : "",
        business.socials?.instagram ? "Instagram" : "",
        business.socials?.facebook ? "Facebook" : "",
        business.socials?.youtube ? "YouTube" : "",
        business.socials?.linkedin ? "LinkedIn" : "",
        business.socials?.tiktok ? "TikTok" : "",
        business.socials?.website ? "Sitio web" : "",
      ].filter(Boolean);
  const footerHasVisibleContent = Boolean(
    footerHasBrand ||
    footerHasNavigation ||
    footerHasContact ||
    footerSocialLabels.length ||
    footerCopyright
  );
  const width = previewDevice.width;
  const scaleFor = (sectionId, kind) => segmentVisualScale(theme, sectionId, kind);
  const navbarStyle = normalizeVisualStyle(
    navigation.style,
    visualStyleFromTemplate(theme.template)
  );
  const footerStyle = normalizeVisualStyle(
    footer.style,
    visualStyleFromTemplate(theme.template)
  );
  const footerLogoPosition = normalizeImagePosition(footer.logoPosition);
  const navbarDark = navbarStyle === "style-3";
  const navbarCommercial = navbarStyle === "style-2";
  const navbarSpacingScale = scaleFor("navbar", "spacing");
  const navbarImageScale = scaleFor("navbar", "image");
  const navbarTitleScale = scaleFor("navbar", "title");
  const heroSpacingScale = scaleFor("hero", "spacing");
  const heroImageScale = scaleFor("hero", "image");
  const heroTitleScale = scaleFor("hero", "title");
  const spotlightSpacingScale = scaleFor("spotlight", "spacing");
  const spotlightImageScale = scaleFor("spotlight", "image");
  const spotlightTitleScale = scaleFor("spotlight", "title");
  const featuredSpacingScale = scaleFor("featured", "spacing");
  const featuredImageScale = scaleFor("featured", "image");
  const featuredTitleScale = scaleFor("featured", "title");
  const brandsSpacingScale = scaleFor("brands", "spacing");
  const brandsImageScale = scaleFor("brands", "image");
  const brandsTitleScale = scaleFor("brands", "title");
  const categoriesSpacingScale = scaleFor("categories", "spacing");
  const categoriesTitleScale = scaleFor("categories", "title");
  const aboutSpacingScale = scaleFor("about", "spacing");
  const aboutImageScale = scaleFor("about", "image");
  const aboutTitleScale = scaleFor("about", "title");
  const highlightsSpacingScale = scaleFor("highlights", "spacing");
  const highlightsImageScale = scaleFor("highlights", "image");
  const highlightsTitleScale = scaleFor("highlights", "title");
  const contactSpacingScale = scaleFor("contact", "spacing");
  const contactImageScale = scaleFor("contact", "image");
  const contactTitleScale = scaleFor("contact", "title");
  const footerSpacingScale = scaleFor("footer", "spacing");
  const footerImageScale = scaleFor("footer", "image");
  const footerTitleScale = scaleFor("footer", "title");

  const openImageEditor = (sectionId, imagePath) => (event) => {
    if (!studioMode || !onOpenSegmentTool) return;
    event?.stopPropagation?.();
    onSelectSection?.(sectionId);
    onOpenSegmentTool(sectionId, "image", imagePath || "");
  };

  const editableImageProps = (sectionId, imagePath, editLabel) => (
    studioMode && onSelectElement
      ? { landingField: imagePath, editLabel }
      : studioMode && onOpenSegmentTool
        ? { onEdit: openImageEditor(sectionId, imagePath), editLabel }
        : {}
  );

  const hiddenNavbarTarget = (fieldPath, label) => studioMode ? (
    <Button
      key={`hidden:${fieldPath}`}
      size="small"
      variant="text"
      data-landing-field={fieldPath}
      data-landing-element-type="button"
      data-landing-edit-label={`Editar ${label}`}
      sx={{
        minWidth: 0,
        px: 0.85,
        py: 0.25,
        border: "1px dashed currentColor",
        borderRadius: 999,
        opacity: 0.52,
        textTransform: "none",
        fontSize: ".72rem",
        whiteSpace: "nowrap",
      }}
    >
      {label} · oculto
    </Button>
  ) : null;


  const studioTarget = ({ fieldPath = "", elementId = "", label, count = null, muted = false }) => studioMode ? (
    <Button
      key={fieldPath || elementId}
      size="small"
      variant="text"
      data-landing-field={fieldPath || undefined}
      data-landing-element-id={elementId || undefined}
      data-landing-element-type={elementId ? "complex" : (/CtaLabel$/.test(fieldPath) ? "button" : "text")}
      data-landing-edit-label={`Editar ${label}`}
      sx={{
        minWidth: 0,
        px: 0.9,
        py: 0.35,
        border: "1px dashed rgba(255,255,255,.58)",
        borderRadius: 999,
        bgcolor: "rgba(25,36,47,.66)",
        color: "#fff",
        opacity: muted ? 0.7 : 1,
        textTransform: "none",
        fontSize: ".72rem",
        fontWeight: 850,
        backdropFilter: "blur(8px)",
        "&:hover": { bgcolor: "rgba(25,36,47,.84)" },
      }}
    >
      {label}{count === null ? "" : ` · ${count}`}
    </Button>
  ) : null;

  return (
    <Box
      data-landing-preview-device={previewDevice.id}
      data-landing-preview-width={width}
      sx={{
        width: bare ? "auto" : `${width}px`,
        maxWidth: bare ? undefined : "none",
        minHeight: "100%",
        bgcolor: background,
        color: text,
        overflow: "hidden",
        position: "relative",
        display: bare ? "contents" : "flex",
        flexDirection: bare ? undefined : "column",
        boxShadow: mobileLayout ? shadows.lg : "none",
        borderRadius: mobileLayout ? px(borders.radiusLg, 18) : 0,
        fontFamily: typography.bodyFontFamily || typography.fontFamily || "Asap, sans-serif",
        fontSize: px(typography.baseFontSize, 16),
        lineHeight: finite(typography.lineHeight, 1.5),
        "& h1, & h2, & h3, & h4, & h5, & h6": {
          fontFamily: typography.headingFontFamily || typography.fontFamily || "inherit",
          lineHeight: finite(typography.headingLineHeight, 1.15),
          letterSpacing: px(typography.letterSpacing, 0),
        },
        "& .MuiButton-root": {
          borderRadius: px(borders.radiusPill, 9999),
          fontWeight: finite(typography.fontWeightMedium, 600),
        },
      }}
    >
      {segmentAllowed("navbar") && sections.showNavbar !== false ? (
        <SectionFrame
          sectionId="navbar"
          layoutOrder={orderFor("navbar")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("navbar")}
          sx={{ ...segmentCssVariables(theme, "navbar"), ...elementColorSxForSection("navbar") }}
        >
          {navbarCommercial && navbarBusinessInfo ? (
            <Box sx={{ px: horizontalPadding, py: 0.55 * navbarSpacingScale, bgcolor: navbarColors.secondary, color: navbarColors.onSecondary }}>
              <Typography data-landing-field="business.phone" variant="caption" sx={{ opacity: 0.82 }}>
                {navbarBusinessInfo}
              </Typography>
            </Box>
          ) : null}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
            sx={{
              px: horizontalPadding,
              py: px(finite(spacing.md, 16) * navbarSpacingScale, 16),
              bgcolor: Object.keys(sectionBackgroundSx("navbar")).length ? "transparent" : (navbarDark ? secondary : surface),
              color: navbarDark ? navbarColors.onSecondary : navbarColors.text,
              borderBottom: cardBorder,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 0 }}>
              {navbarLogo ? (
                <Box
                  data-landing-element-id="navbar.logo"
                  data-landing-element-type="image"
                  data-landing-edit-label="Editar logo"
                  sx={{ display: "inline-flex", minWidth: 0 }}
                >
                  <SafeCanvasImage
                    src={navbarLogo}
                    alt=""
                    monochromeColor={navbarLogoColor}
                    sx={navbarLogoMaxHeight !== null
                      ? {
                          display: "block",
                          width: "auto",
                          maxWidth: desktopLayout ? 220 : "min(42vw, 150px)",
                          height: navbarLogoMaxHeight,
                          maxHeight: navbarLogoMaxHeight,
                          objectFit: "contain",
                        }
                      : { width: 38 * navbarImageScale, height: 38 * navbarImageScale, objectFit: "contain" }}
                  />
                </Box>
              ) : navigation.showLogo === false && studioMode ? (
                <Button
                  size="small"
                  variant="outlined"
                  data-landing-element-id="navbar.logo"
                  data-landing-element-type="image"
                  data-landing-edit-label="Editar logo"
                  sx={{ textTransform: "none", borderStyle: "dashed", opacity: 0.72 }}
                >
                  Logo oculto
                </Button>
              ) : business.name ? (
                <Stack direction="row" spacing={0.7} alignItems="center" sx={{ minWidth: 0 }}>
                  <Typography
                    data-landing-field="business.name"
                    data-landing-edit-label="Editar nombre del negocio"
                    noWrap
                    sx={{
                      fontSize: `${finite(typography.baseFontSize, 16) * navbarTitleScale}px`,
                      fontWeight: "var(--wp-segment-title-weight)",
                      letterSpacing: "var(--wp-segment-letter-spacing)",
                      color: navbarDark ? navbarColors.onSecondary : navbarColors.secondary,
                    }}
                  >
                    {business.name}
                  </Typography>
                  {studioMode && navigation.showLogo !== false ? (
                    <Button
                      size="small"
                      data-landing-element-id="navbar.logo"
                      data-landing-element-type="image"
                      data-landing-edit-label="Agregar logo"
                      sx={{ minWidth: 0, px: 0.8, textTransform: "none", fontSize: ".72rem" }}
                    >
                      + Logo
                    </Button>
                  ) : null}
                </Stack>
              ) : studioMode ? (
                <Button
                  size="small"
                  data-landing-element-id="navbar.logo"
                  data-landing-element-type="image"
                  data-landing-edit-label="Agregar logo"
                  sx={{ textTransform: "none", borderStyle: "dashed" }}
                >
                  Agregar logo
                </Button>
              ) : null}
            </Stack>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ display: desktopLayout ? "flex" : "none", minWidth: 0 }}>
              {navigation.homeLabel ? (
                <Typography data-landing-field="navigation.homeLabel" data-landing-element-type="button" data-landing-edit-label="Editar Inicio" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.homeLabel}</Typography>
              ) : hiddenNavbarTarget("navigation.homeLabel", "Inicio")}

              {footerAboutVisible && navigation.aboutLabel ? (
                <Typography data-landing-field="navigation.aboutLabel" data-landing-element-type="button" data-landing-edit-label="Editar Sobre nosotros" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.aboutLabel}</Typography>
              ) : (navigation.showAboutLink === false || !navigation.aboutLabel) ? hiddenNavbarTarget("navigation.aboutLabel", "Sobre nosotros") : null}

              {footerHighlightsVisible && navigation.highlightsLabel ? (
                <Typography data-landing-field="navigation.highlightsLabel" data-landing-element-type="button" data-landing-edit-label="Editar Diferenciales" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.highlightsLabel}</Typography>
              ) : (navigation.showHighlightsLink === false || !navigation.highlightsLabel) ? hiddenNavbarTarget("navigation.highlightsLabel", "Diferenciales") : null}

              {productsEnabled && navigation.showProductsLink !== false && navigation.productsLabel ? (
                <Typography data-landing-field="navigation.productsLabel" data-landing-element-type="button" data-landing-edit-label="Editar Productos o servicios" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.productsLabel}</Typography>
              ) : productsEnabled ? hiddenNavbarTarget("navigation.productsLabel", "Productos") : null}

              {footerContactVisible && navigation.contactLabel ? (
                <Typography data-landing-field="navigation.contactLabel" data-landing-element-type="button" data-landing-edit-label="Editar Contacto" variant="body2" noWrap sx={{ color: navbarDark ? "rgba(255,255,255,.78)" : navbarColors.mutedText }}>{navigation.contactLabel}</Typography>
              ) : (navigation.showContactLink === false || !navigation.contactLabel) ? hiddenNavbarTarget("navigation.contactLabel", "Contacto") : null}

              {navbarButtons.map((item, index) => (
                <Button
                  data-landing-field={`navigation.buttons.${index}.label`}
                  data-landing-element-type="button"
                  data-landing-edit-label={`Editar botón ${index + 1}`}
                  key={item?.id || `navbar-button-${index}`}
                  size="small"
                  variant="outlined"
                  sx={{
                    minWidth: 0,
                    px: 1.2,
                    py: 0.35,
                    borderRadius: 999,
                    textTransform: "none",
                    fontWeight: 800,
                    color: navbarDark ? navbarColors.onSecondary : navbarColors.mutedText,
                    borderColor: navbarDark ? "rgba(255,255,255,.28)" : navbarColors.divider,
                    whiteSpace: "nowrap",
                  }}
                  onClick={studioMode && !onSelectElement ? (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    onSelectSection?.("navbar");
                  } : undefined}
                >
                  {item.label}
                </Button>
              ))}

              {studioMode ? (
                <Button
                  size="small"
                  data-landing-element-id="navbar.buttons-manager"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Administrar botones"
                  sx={{ minWidth: 0, px: 0.9, textTransform: "none", fontSize: ".72rem", opacity: 0.72 }}
                >
                  Administrar botones
                </Button>
              ) : null}

              {navigation.showCustomerAccess !== false && navigation.loginLabel ? (
                <Typography data-landing-field="navigation.loginLabel" data-landing-element-type="button" data-landing-edit-label="Editar acceso de clientes" variant="body2" noWrap sx={{ fontWeight: 800, color: navbarDark ? navbarColors.onSecondary : navbarColors.secondary }}>{navigation.loginLabel}</Typography>
              ) : hiddenNavbarTarget("navigation.loginLabel", "Acceso clientes")}
            </Stack>
            {!desktopLayout ? (
              <IconButton
                size="small"
                data-landing-element-id="navbar.mobile-menu"
                data-landing-element-type="complex"
                data-landing-edit-label="Editar menú móvil"
                aria-label={navigation.menuAriaLabel || "Abrir menú"}
                onClick={studioMode && !onSelectElement ? (event) => {
                  event.stopPropagation();
                  onSelectSection?.("navbar");
                } : undefined}
                sx={{
                  flexShrink: 0,
                  color: navbarDark ? navbarColors.onSecondary : navbarColors.secondary,
                  border: `1px solid ${navbarDark ? "rgba(255,255,255,.2)" : navbarColors.divider}`,
                  bgcolor: navbarDark ? "rgba(255,255,255,.08)" : navbarColors.background,
                }}
              >
                <MenuRoundedIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {segmentAllowed("hero") && sections.showHero !== false && (heroHasContent || studioMode) ? (
        <SectionFrame
          sectionId="hero"
          layoutOrder={orderFor("hero")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("hero")}
          sx={{
            ...segmentCssVariables(theme, "hero"),
            ...elementColorSxForSection("hero"),
            minHeight: mobileLayout ? 430 : previewDevice.id === "tablet" ? 480 : 520,
            display: "flex",
            alignItems: heroContentAlign,
            justifyContent: heroContentJustify,
            position: "relative",
            overflow: "hidden",
            bgcolor: heroColors.secondary,
            background: `linear-gradient(135deg, ${heroColors.secondary}, ${heroColors.primary})`,
          }}
        >
          {studioMode ? (
            <Stack
              direction="row"
              spacing={0.6}
              useFlexGap
              flexWrap="wrap"
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 8, justifyContent: "flex-end", maxWidth: "72%" }}
            >
              {studioTarget({ elementId: "hero.slides-manager", label: "Slider", count: heroSlideCount, muted: heroSlideCount === 0 })}
              {studioTarget({ elementId: "hero.rotating-phrases", label: "Frases", count: heroPhraseCount, muted: heroPhraseCount === 0 })}
              {!hero.eyebrow ? studioTarget({ fieldPath: "hero.eyebrow", label: "Texto superior", muted: true }) : null}
              {!heroTitle ? studioTarget({ fieldPath: heroTitleFieldPath, label: "Título", muted: true }) : null}
              {!heroSubtitle ? studioTarget({ fieldPath: heroSubtitleFieldPath, label: "Subtítulo", muted: true }) : null}
              {!heroImage ? studioTarget({ fieldPath: "hero.imageUrl", label: "Imagen", muted: true }) : null}
              {!showHeroPrimaryCta ? studioTarget({ fieldPath: "hero.primaryCtaLabel", label: "Botón principal", muted: true }) : null}
              {!showHeroSecondaryCta ? studioTarget({ fieldPath: "hero.secondaryCtaLabel", label: "Botón secundario", muted: true }) : null}
            </Stack>
          ) : null}
          {heroImage ? (
            <SafeCanvasImage
              src={heroImage}
              alt={heroSlide?.alt || heroTitle || business.name || ""}
              sx={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                objectPosition: heroImagePosition,
                transform: `scale(${heroImageScale})`,
                transformOrigin: "center",
              }}
              {...editableImageProps("hero", heroImageSourcePath, "Editar imagen de portada")}
            />
          ) : null}
          {hero.overlay ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                background: hero.overlay,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <Box
            sx={{
              position: "relative",
              zIndex: 1,
              px: responsive(
                { xs: px(spacing.lg, 24), sm: px(spacing.xxl, 48), md: px(spacing.section, 72) },
                px(spacing.lg, 24)
              ),
              py: responsive(
                {
                  xs: px(finite(spacing.xl, 32) * heroSpacingScale, 32),
                  md: px(finite(spacing.section, 72) * heroSpacingScale, 72),
                },
                px(finite(spacing.xl, 32) * heroSpacingScale, 32)
              ),
              width: "100%",
              maxWidth: 780,
              textAlign: heroTextAlign,
              color: heroColors.onSecondary,
            }}
          >
            {hero.eyebrow ? <Typography data-landing-field="hero.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ opacity: .78, fontWeight: 900 }}>{hero.eyebrow}</Typography> : null}
            {heroTitle ? (
              <Typography
                data-landing-field={heroTitleFieldPath}
                data-landing-edit-label="Editar título principal"
                sx={{
                  mt: hero.eyebrow ? .4 : 0,
                  fontFamily: typography.headingFontFamily || "inherit",
                  fontSize: responsive(
                    {
                      xs: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 1.85 * heroTitleScale}px`,
                      md: `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 3.1 * heroTitleScale}px`,
                    },
                    `${finite(typography.baseFontSize, 16) * finite(typography.scaleRatio, 1.2) * 1.85 * heroTitleScale}px`
                  ),
                  lineHeight: finite(typography.headingLineHeight, 1.15),
                  fontWeight: "var(--wp-segment-title-weight)",
                  letterSpacing: "var(--wp-segment-letter-spacing)",
                  overflowWrap: "anywhere",
                }}
              >
                {heroTitle}
              </Typography>
            ) : null}
            {heroRotatingPhrase ? (
              <Typography
                data-landing-element-id="hero.rotating-phrases"
                data-landing-element-type="complex"
                data-landing-edit-label="Editar frases rotativas"
                sx={{
                  mt: 0.8,
                  fontFamily: typography.headingFontFamily || "inherit",
                  fontWeight: 850,
                  fontSize: responsive({ xs: "1.1rem", md: "1.45rem" }, "1.1rem"),
                  color: heroColors.primary,
                  textShadow: "0 2px 12px rgba(0,0,0,.32)",
                }}
              >
                {heroRotatingPhrase}
              </Typography>
            ) : null}
            {heroSubtitle ? <Typography data-landing-field={heroSubtitleFieldPath} data-landing-edit-label="Editar texto de apoyo" sx={{ mt: heroTitle || hero.eyebrow ? 1.5 : 0, maxWidth: 650, ml: heroHorizontalPosition === "right" ? "auto" : 0, mr: heroHorizontalPosition === "left" ? "auto" : 0, fontSize: responsive({ xs: ".98rem", md: "1.12rem" }, ".98rem"), lineHeight: 1.65, opacity: .88 }}>{heroSubtitle}</Typography> : null}
            {showHeroPrimaryCta || showHeroSecondaryCta ? (
              <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={1.2} sx={{ mt: heroTitle || heroSubtitle || hero.eyebrow ? 2.4 : 0, alignItems: responsive({ xs: "stretch", sm: "center" }, "stretch"), justifyContent: responsive({ xs: "stretch", sm: heroContentJustify }, "stretch") }}>
                {showHeroPrimaryCta ? (
                  <Button
                    data-landing-field="hero.primaryCtaLabel"
                    data-landing-edit-label="Editar botón principal"
                    variant="contained"
                    startIcon={hero.primaryCtaShowIcon !== false ? <HeroCanvasCtaIcon name={hero.primaryCtaIcon} fallback="explore" /> : undefined}
                    sx={heroCtaCanvasSx(hero.primaryCtaStyle, { outlined: false, colors: heroColors, fallbackShadow: shadows.sm })}
                  >
                    {hero.primaryCtaLabel}
                  </Button>
                ) : null}
                {showHeroSecondaryCta ? (
                  <Button
                    data-landing-field="hero.secondaryCtaLabel"
                    data-landing-edit-label="Editar botón secundario"
                    variant="outlined"
                    startIcon={hero.secondaryCtaShowIcon !== false ? <HeroCanvasCtaIcon name={hero.secondaryCtaIcon} fallback="storefront" /> : undefined}
                    sx={heroCtaCanvasSx(hero.secondaryCtaStyle, { outlined: true, colors: heroColors, fallbackShadow: "none" })}
                  >
                    {hero.secondaryCtaLabel}
                  </Button>
                ) : null}
              </Stack>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("spotlight") && productsEnabled && sections.showProducts !== false && sections.showSpotlight !== false && catalog.spotlight?.enabled && (spotlightHasContent || studioMode) ? (
        <SectionFrame
          sectionId="spotlight"
          layoutOrder={orderFor("spotlight")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("spotlight")}
          sx={{
            ...segmentCssVariables(theme, "spotlight"),
            ...elementColorSxForSection("spotlight"),
            position: "relative",
            overflow: "hidden",
            p: responsive({ xs: 2 * spotlightSpacingScale, md: 4 * spotlightSpacingScale }, 2 * spotlightSpacingScale),
            color: "#fff",
            background: spotlightBackground
              ? `linear-gradient(90deg, ${colorWithAlpha((catalog.spotlight.secondaryColor || spotlightColors.secondary), "F2")}, ${colorWithAlpha((catalog.spotlight.primaryColor || spotlightColors.primary), "D9")}), url(${spotlightBackground}) ${spotlightBackgroundPosition}/${Math.round(spotlightImageScale * 100)}% auto no-repeat`
              : `linear-gradient(100deg, ${catalog.spotlight.secondaryColor || spotlightColors.secondary}, ${catalog.spotlight.primaryColor || spotlightColors.primary})`,
          }}
        >
          {studioMode ? (
            <Stack
              direction="row"
              spacing={0.6}
              useFlexGap
              flexWrap="wrap"
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 8, justifyContent: "flex-end", maxWidth: "76%" }}
            >
              {studioTarget({ fieldPath: "catalog.spotlight.backgroundImageUrl", label: "Fondo", muted: !spotlightBackground })}
              {!spotlightLogo ? studioTarget({ fieldPath: "catalog.spotlight.logoUrl", label: "Logo", muted: true }) : null}
              {!catalog.spotlight.eyebrow ? studioTarget({ fieldPath: "catalog.spotlight.eyebrow", label: "Texto superior", muted: true }) : null}
              {!catalog.spotlight.title ? studioTarget({ fieldPath: "catalog.spotlight.title", label: "Título", muted: true }) : null}
              {!catalog.spotlight.description ? studioTarget({ fieldPath: "catalog.spotlight.description", label: "Descripción", muted: true }) : null}
              {!showSpotlightCta ? studioTarget({ fieldPath: "catalog.spotlight.ctaLabel", label: "Botón", muted: true }) : null}
            </Stack>
          ) : null}
          <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={2.4} alignItems={responsive({ xs: "stretch", sm: "center" }, "stretch")} sx={{ position: "relative", zIndex: 1 }}>
            {spotlightLogo ? (
              <Box sx={{ width: responsive({ xs: 210 * spotlightImageScale, sm: 250 * spotlightImageScale }, 210 * spotlightImageScale), p: 1.4, bgcolor: "#fff", borderRadius: 2, flexShrink: 0 }}>
                <SafeCanvasImage
                  src={spotlightLogo}
                  alt={catalog.spotlight.logoAlt || ""}
                  sx={{ width: "100%", height: 72 * spotlightImageScale, objectFit: "contain" }}
                  {...editableImageProps("spotlight", "catalog.spotlight.logoUrl", "Editar logo o imagen destacada")}
                />
              </Box>
            ) : null}
            {catalog.spotlight.eyebrow || catalog.spotlight.title || catalog.spotlight.description ? (
              <Box sx={{ flex: 1, minWidth: 0 }}>
                {catalog.spotlight.eyebrow ? <Typography data-landing-field="catalog.spotlight.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ opacity: .75 }}>{catalog.spotlight.eyebrow}</Typography> : null}
                {catalog.spotlight.title ? <Typography data-landing-field="catalog.spotlight.title" data-landing-edit-label="Editar título" sx={{ fontSize: responsive({ xs: 30 * spotlightTitleScale, md: 34 * spotlightTitleScale }, 30 * spotlightTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.spotlight.title}</Typography> : null}
                {catalog.spotlight.description ? <Typography data-landing-field="catalog.spotlight.description" data-landing-edit-label="Editar descripción" sx={{ mt: catalog.spotlight.title || catalog.spotlight.eyebrow ? .8 : 0, opacity: .82 }}>{catalog.spotlight.description}</Typography> : null}
              </Box>
            ) : null}
            {showSpotlightCta ? (
              <Button data-landing-field="catalog.spotlight.ctaLabel" data-landing-edit-label="Editar botón destacado" variant="contained" sx={{ bgcolor: "#fff", color: catalog.spotlight.primaryColor || spotlightColors.primary, textTransform: "none", flexShrink: 0 }}>{catalog.spotlight.ctaLabel}</Button>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {segmentAllowed("featured") && productsEnabled && sections.showProducts !== false && sections.showFeatured !== false ? (
        <SectionFrame sectionId="featured" layoutOrder={orderFor("featured")} activeSection={activeSection} selectedElement={selectedElement} studioMode={studioMode} onSelectSection={onSelectSection} onSelectElement={onSelectElement} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("featured")} sx={{ ...segmentCssVariables(theme, "featured"), ...elementColorSxForSection("featured"), px: horizontalPadding, py: responsive({ xs: px(finite(spacing.xl, 32) * featuredSpacingScale, 32), md: px(finite(spacing.section, 72) * featuredSpacingScale, 72) }, px(finite(spacing.xl, 32) * featuredSpacingScale, 32)), bgcolor: featuredColors.background }}>
          {catalog.featured?.eyebrow ? <Typography data-landing-field="catalog.featured.eyebrow" variant="overline" sx={{ color: featuredColors.primary, fontWeight: 900 }}>{catalog.featured.eyebrow}</Typography> : studioTarget({ fieldPath: "catalog.featured.eyebrow", label: "Texto superior", muted: true })}
          {catalog.featured?.title ? <Typography data-landing-field="catalog.featured.title" sx={{ color: featuredColors.secondary, fontSize: responsive({ xs: 30 * featuredTitleScale, md: 34 * featuredTitleScale }, 30 * featuredTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.featured.title}</Typography> : studioTarget({ fieldPath: "catalog.featured.title", label: "Título", muted: true })}
          {catalog.featured?.subtitle ? <Typography data-landing-field="catalog.featured.subtitle" sx={{ mt: catalog.featured?.title || catalog.featured?.eyebrow ? .6 : 0, color: featuredColors.mutedText }}>{catalog.featured.subtitle}</Typography> : studioTarget({ fieldPath: "catalog.featured.subtitle", label: "Descripción", muted: true })}
          <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1 }}>
            {catalog.featured?.ctaLabel ? (
              <Button
                size="small"
                variant="outlined"
                data-landing-field="catalog.featured.ctaLabel"
                data-landing-element-type="button"
                data-landing-edit-label="Editar botón de catálogo"
                title="Acción del catálogo"
                sx={{ textTransform: "none", fontWeight: 850, borderRadius: 999 }}
              >
                {catalog.featured.ctaLabel}
              </Button>
            ) : studioTarget({ fieldPath: "catalog.featured.ctaLabel", label: "Botón de catálogo", muted: true })}
            {studioMode ? <Chip size="small" label="Catálogo" variant="outlined" /> : null}
          </Stack>
          <PlaceholderCards labels={["Producto o servicio 1", "Producto o servicio 2", "Producto o servicio 3"]} primary={primary} surface={surface} tokens={tokens} device={previewDevice.id} cardConfig={catalogCards} imageScale={featuredImageScale} spacingScale={featuredSpacingScale} landingElementId="featured.cards" />
        </SectionFrame>
      ) : null}

      {segmentAllowed("brands") && productsEnabled && sections.showProducts !== false && sections.showBrands !== false ? (
        <SectionFrame sectionId="brands" layoutOrder={orderFor("brands")} activeSection={activeSection} selectedElement={selectedElement} studioMode={studioMode} onSelectSection={onSelectSection} onSelectElement={onSelectElement} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("brands")} sx={{ ...segmentCssVariables(theme, "brands"), ...elementColorSxForSection("brands"), px: horizontalPadding, py: px(finite(spacing.xl, 32) * brandsSpacingScale, 32), bgcolor: brandsColors.surface }}>
          {catalog.brands?.eyebrow ? <Typography data-landing-field="catalog.brands.eyebrow" variant="overline" sx={{ color: brandsColors.primary, fontWeight: 900 }}>{catalog.brands.eyebrow}</Typography> : studioTarget({ fieldPath: "catalog.brands.eyebrow", label: "Texto superior", muted: true })}
          {catalog.brands?.title ? <Typography data-landing-field="catalog.brands.title" sx={{ color: featuredColors.secondary, fontSize: 30 * brandsTitleScale, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.brands.title}</Typography> : studioTarget({ fieldPath: "catalog.brands.title", label: "Título", muted: true })}
          {catalog.brands?.subtitle ? <Typography data-landing-field="catalog.brands.subtitle" sx={{ mt: catalog.brands?.title || catalog.brands?.eyebrow ? .6 : 0, color: brandsColors.mutedText }}>{catalog.brands.subtitle}</Typography> : studioTarget({ fieldPath: "catalog.brands.subtitle", label: "Descripción", muted: true })}

          {configuredBrands.length ? (
            <Stack
              direction="row"
              spacing={1.2}
              useFlexGap
              flexWrap="wrap"
              data-landing-element-id="brands.items"
              data-landing-element-type="collection"
              data-landing-edit-label="Administrar marcas"
              sx={{ mt: catalog.brands?.eyebrow || catalog.brands?.title || catalog.brands?.subtitle ? 1.8 : 0 }}
            >
              {configuredBrands.map(({ item, sourceIndex }, index) => {
                const label = item?.name ?? item?.label ?? "";
                const brandLogo = resolveLandingAssetUrl(item?.logoUrl);
                const itemPath = `catalog.brands.items.${sourceIndex}`;
                return (
                  <Paper
                    key={`${label}-${sourceIndex >= 0 ? sourceIndex : index}`}
                    component={studioMode ? "button" : "div"}
                    type={studioMode ? "button" : undefined}
                    aria-label={studioMode ? `Editar marca ${label || sourceIndex + 1}` : undefined}
                    data-landing-field={studioMode ? itemPath : undefined}
                    data-landing-element-type={studioMode ? "collection" : undefined}
                    data-landing-edit-label={studioMode ? `Editar marca ${label || sourceIndex + 1}` : undefined}
                    elevation={0}
                    sx={{
                      width: responsive({ xs: 126 * brandsImageScale, sm: 150 * brandsImageScale }, 126 * brandsImageScale),
                      minHeight: 76 * brandsImageScale,
                      px: 1.5,
                      py: 1.2,
                      display: "grid",
                      placeItems: "center",
                      borderRadius: px(borders.radiusMd, 10),
                      bgcolor: brandsColors.background,
                      border: cardBorder,
                      boxShadow: shadows.sm,
                      font: "inherit",
                      color: "inherit",
                      cursor: studioMode ? "pointer" : "default",
                      appearance: "none",
                      textAlign: "inherit",
                    }}
                  >
                    {brandLogo ? (
                      <SafeCanvasImage
                        src={brandLogo}
                        alt={item?.alt || label}
                        sx={{ width: "100%", height: 48 * brandsImageScale, objectFit: "contain" }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ textAlign: "center", fontWeight: 850, color: brandsColors.secondary }}>{label || `Marca ${sourceIndex + 1}`}</Typography>
                    )}
                  </Paper>
                );
              })}
            </Stack>
          ) : (
            <Stack sx={{ mt: catalog.brands?.eyebrow || catalog.brands?.title || catalog.brands?.subtitle ? 1.8 : 0 }}>
              {studioTarget({ elementId: "brands.items", label: "Agregar marcas", count: 0 })}
            </Stack>
          )}

          {studioMode ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.4 }}>
              {configuredBrands.length ? studioTarget({ elementId: "brands.items", label: "Administrar marcas", count: configuredBrands.length }) : null}
              <Button
                size="small"
                variant="outlined"
                data-landing-field="catalog.brands.ctaLabel"
                data-landing-element-type="button"
                data-landing-edit-label="Editar acción de marca"
                sx={{ textTransform: "none", fontWeight: 850, borderRadius: 999 }}
              >
                Acción: {catalog.brands?.ctaLabel || "Abrir marca"}
              </Button>
              <Chip size="small" label="Abre catálogo" variant="outlined" />
            </Stack>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("categories") && productsEnabled && sections.showProducts !== false && sections.showCategories !== false ? (
        <SectionFrame sectionId="categories" layoutOrder={orderFor("categories")} activeSection={activeSection} selectedElement={selectedElement} studioMode={studioMode} onSelectSection={onSelectSection} onSelectElement={onSelectElement} onOpenSegmentTool={onOpenSegmentTool} onToggleSection={onToggleSection} device={previewDevice.id} backgroundSx={sectionBackgroundSx("categories")} sx={{ ...segmentCssVariables(theme, "categories"), ...elementColorSxForSection("categories"), px: horizontalPadding, py: px(finite(spacing.xl, 32) * categoriesSpacingScale, 32), bgcolor: categoriesColors.surface }}>
          {catalog.categories?.eyebrow ? <Typography data-landing-field="catalog.categories.eyebrow" variant="overline" sx={{ color: categoriesColors.primary, fontWeight: 900 }}>{catalog.categories.eyebrow}</Typography> : null}
          {catalog.categories?.title ? <Typography data-landing-field="catalog.categories.title" sx={{ color: categoriesColors.secondary, fontSize: 30 * categoriesTitleScale, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{catalog.categories.title}</Typography> : null}
          {catalog.categories?.subtitle ? <Typography data-landing-field="catalog.categories.subtitle" sx={{ mt: catalog.categories?.title || catalog.categories?.eyebrow ? .6 : 0, color: categoriesColors.mutedText }}>{catalog.categories.subtitle}</Typography> : null}

          {studioMode && (!catalog.categories?.eyebrow || !catalog.categories?.title || !catalog.categories?.subtitle) ? (
            <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
              {!catalog.categories?.eyebrow ? studioTarget({ fieldPath: "catalog.categories.eyebrow", label: "Texto superior", muted: true }) : null}
              {!catalog.categories?.title ? studioTarget({ fieldPath: "catalog.categories.title", label: "Título", muted: true }) : null}
              {!catalog.categories?.subtitle ? studioTarget({ fieldPath: "catalog.categories.subtitle", label: "Descripción", muted: true }) : null}
            </Stack>
          ) : null}

          <Stack direction="row" alignItems="center" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: catalog.categories?.title || catalog.categories?.eyebrow || catalog.categories?.subtitle ? 1.5 : 0 }}>
            {studioMode ? <Chip size="small" label="Se genera desde tu catálogo" variant="outlined" /> : null}
            {studioMode ? studioTarget({ elementId: "categories.item-naming", label: "Cómo llamamos a tus ítems" }) : null}
          </Stack>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: responsive({ xs: "1fr", sm: "repeat(3, minmax(0, 1fr))" }, "1fr"),
              gap: 1,
              mt: 1.2,
            }}
          >
            {[0, 1, 2].map((item) => {
              const singular = String(catalog.categories?.productSingular || "producto").trim() || "producto";
              const plural = String(catalog.categories?.productPlural || "productos").trim() || "productos";
              const count = item === 0 ? 1 : (item + 1) * 6;
              return (
                <Paper
                  key={item}
                  component={studioMode ? "button" : "div"}
                  type={studioMode ? "button" : undefined}
                  data-landing-element-id={studioMode ? "categories.generated" : undefined}
                  data-landing-element-type={studioMode ? "complex" : undefined}
                  data-landing-edit-label={studioMode ? "Ver origen de las categorías" : undefined}
                  aria-label={studioMode ? "Categoría generada desde tu catálogo. Ver configuración" : undefined}
                  elevation={0}
                  sx={{
                    minWidth: 0,
                    p: 1.25,
                    display: "grid",
                    gap: 0.45,
                    textAlign: "left",
                    color: "inherit",
                    font: "inherit",
                    appearance: "none",
                    bgcolor: categoriesColors.background,
                    border: cardBorder,
                    borderRadius: px(borders.radiusMd, 10),
                    cursor: studioMode ? "pointer" : "default",
                  }}
                >
                  <Typography variant="caption" sx={{ color: categoriesColors.primary, fontWeight: 900 }}>
                    Categoría del catálogo
                  </Typography>
                  <Typography sx={{ color: categoriesColors.secondary, fontWeight: 850 }}>
                    {`Ejemplo ${item + 1}`}
                  </Typography>
                  <Typography variant="caption" sx={{ color: categoriesColors.mutedText }}>
                    {count} {count === 1 ? singular : plural}
                  </Typography>
                </Paper>
              );
            })}
          </Box>

          {studioMode ? (
            <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 1.2 }}>
              {studioTarget({ elementId: "categories.generated", label: "Categorías automáticas" })}
              <Button
                size="small"
                variant="outlined"
                data-landing-field="catalog.categories.ctaLabel"
                data-landing-element-type="button"
                data-landing-edit-label="Editar acción de categoría"
                sx={{ textTransform: "none", fontWeight: 850, borderRadius: 999 }}
              >
                Acción: {catalog.categories?.ctaLabel || "Ver productos"}
              </Button>
              <Chip size="small" label="Abre categoría" variant="outlined" />
            </Stack>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("about") && sections.showAbout !== false && (aboutHasContent || studioMode) ? (
        <SectionFrame
          sectionId="about"
          layoutOrder={orderFor("about")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("about")}
          sx={{
            ...segmentCssVariables(theme, "about"),
            ...elementColorSxForSection("about"),
            display: "grid",
            gridTemplateColumns: responsive({ xs: "1fr", md: aboutImage && aboutHasCopy ? (about.layout === "image-right" ? "1.1fr .9fr" : ".9fr 1.1fr") : "1fr" }, "1fr"),
            bgcolor: aboutAppearance.backgroundColor || aboutColors.background,
          }}
        >
          {studioMode ? (
            <Stack
              direction="row"
              spacing={0.6}
              useFlexGap
              flexWrap="wrap"
              sx={{ position: "absolute", top: 10, right: 10, zIndex: 8, justifyContent: "flex-end", maxWidth: "78%" }}
            >
              {!about.eyebrow ? studioTarget({ fieldPath: "about.eyebrow", label: "Texto superior", muted: true }) : null}
              {!about.title ? studioTarget({ fieldPath: "about.title", label: "Título", muted: true }) : null}
              {!about.description ? studioTarget({ fieldPath: "about.description", label: "Descripción", muted: true }) : null}
              {!aboutImage ? studioTarget({ fieldPath: "about.imageUrl", label: "Imagen", muted: true }) : null}
              {!showAboutCta ? studioTarget({ fieldPath: "about.ctaLabel", label: "Botón", muted: true }) : null}
            </Stack>
          ) : null}
          {aboutImage ? (
          <Box
            data-landing-field="about.imageUrl"
            data-landing-element-type="image"
            data-landing-edit-label="Editar imagen"
            sx={{
              minHeight: responsive({ xs: 220 * aboutImageScale, md: 390 * aboutImageScale }, 220 * aboutImageScale),
              order: about.layout === "image-right" && desktopLayout ? 2 : undefined,
              bgcolor: colorWithAlpha(primary, "18"),
              backgroundImage: aboutImage ? `url(${aboutImage})` : "none",
              backgroundSize: "cover",
              backgroundPosition: normalizeImagePosition(about.imagePosition),
              ...(studioMode && aboutImage ? {
                cursor: "pointer",
                outline: "2px solid transparent",
                outlineOffset: -2,
                transition: "outline-color .15s ease, filter .15s ease",
                "&:hover": { outlineColor: "rgba(243,90,58,.92)", filter: "brightness(.96)" },
                "&:focus-visible": { outlineColor: "rgba(243,90,58,1)" },
              } : {}),
            }}
          />
          ) : null}
          {(aboutHasCopy || studioMode) ? (
            <Box
              sx={{
                p: responsive({ xs: px(finite(spacing.xl, 32) * aboutSpacingScale, 32), md: px(finite(spacing.section, 72) * aboutSpacingScale, 72) }, px(finite(spacing.xl, 32) * aboutSpacingScale, 32)),
                alignSelf: "center",
                order: about.layout === "image-right" && aboutImage && desktopLayout ? 1 : undefined,
                bgcolor: aboutAppearance.surfaceColor || "transparent",
                borderRadius: aboutAppearance.surfaceColor ? px(borders.radiusLg, 18) : 0,
              }}
            >
              {about.eyebrow ? <Typography data-landing-field="about.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ color: aboutAppearance.accentColor || aboutColors.primary, fontWeight: 900 }}>{about.eyebrow}</Typography> : null}
              {about.title ? <Typography data-landing-field="about.title" data-landing-edit-label="Editar título" sx={{ color: aboutAppearance.titleColor || aboutColors.secondary, fontSize: responsive({ xs: 30 * aboutTitleScale, md: 34 * aboutTitleScale }, 30 * aboutTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{about.title}</Typography> : null}
              {about.description ? <Typography data-landing-field="about.description" data-landing-edit-label="Editar descripción" sx={{ mt: about.title || about.eyebrow ? 1.2 : 0, color: aboutAppearance.textColor || aboutColors.mutedText, lineHeight: 1.7, whiteSpace: "pre-line" }}>{about.description}</Typography> : null}
              {showAboutCta ? <Button data-landing-field="about.ctaLabel" data-landing-element-type="button" data-landing-edit-label="Editar botón" variant="contained" sx={{ mt: about.title || about.eyebrow || about.description ? 2.2 : 0, bgcolor: aboutAppearance.accentColor || aboutColors.primary, color: aboutColors.onPrimary, textTransform: "none", boxShadow: shadows.sm }}>{about.ctaLabel}</Button> : null}
            </Box>
          ) : null}
        </SectionFrame>
      ) : null}

      {segmentAllowed("highlights") && sections.showHighlights !== false && (highlightsHasContent || studioMode) ? (
        <SectionFrame
          sectionId="highlights"
          layoutOrder={orderFor("highlights")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={highlightsBackground ? {} : sectionBackgroundSx("highlights")}
          sx={{
            ...segmentCssVariables(theme, "highlights"),
            ...elementColorSxForSection("highlights"),
            position: "relative",
            overflow: "hidden",
            px: horizontalPadding,
            py: responsive(
              { xs: px(finite(spacing.xl, 32) * highlightsSpacingScale, 32), md: px(finite(spacing.section, 72) * highlightsSpacingScale, 72) },
              px(finite(spacing.xl, 32) * highlightsSpacingScale, 32)
            ),
            bgcolor: highlightsAppearance.backgroundColor || highlightsColors.surface,
          }}
        >
          {highlightsBackground ? (
            <Box
              aria-hidden
              sx={{
                position: "absolute",
                inset: 0,
                backgroundImage: `url(${highlightsBackground})`,
                backgroundSize: "cover",
                backgroundPosition: highlightsBackgroundPosition,
                backgroundRepeat: "no-repeat",
                opacity: highlightsBackgroundOpacity,
                pointerEvents: "none",
              }}
            />
          ) : null}
          <Box sx={{ position: "relative", zIndex: 1 }}>
            {highlightsIntro.eyebrow ? <Typography data-landing-field="highlightsIntro.eyebrow" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ color: highlightsAppearance.accentColor || highlightsColors.primary, fontWeight: 900 }}>{highlightsIntro.eyebrow}</Typography> : null}
            {highlightsIntro.title ? <Typography data-landing-field="highlightsIntro.title" data-landing-edit-label="Editar título" sx={{ color: highlightsAppearance.titleColor || highlightsColors.secondary, fontSize: responsive({ xs: 30 * highlightsTitleScale, md: 34 * highlightsTitleScale }, 30 * highlightsTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{highlightsIntro.title}</Typography> : null}
            {highlightsIntro.subtitle ? <Typography data-landing-field="highlightsIntro.subtitle" data-landing-edit-label="Editar descripción" sx={{ mt: highlightsIntro.title || highlightsIntro.eyebrow ? .7 : 0, color: highlightsAppearance.textColor || highlightsColors.mutedText }}>{highlightsIntro.subtitle}</Typography> : null}
            {studioMode && (!highlightsIntro.eyebrow || !highlightsIntro.title || !highlightsIntro.subtitle) ? (
              <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                {!highlightsIntro.eyebrow ? studioTarget({ fieldPath: "highlightsIntro.eyebrow", label: "Texto superior", muted: true }) : null}
                {!highlightsIntro.title ? studioTarget({ fieldPath: "highlightsIntro.title", label: "Título", muted: true }) : null}
                {!highlightsIntro.subtitle ? studioTarget({ fieldPath: "highlightsIntro.subtitle", label: "Descripción", muted: true }) : null}
              </Stack>
            ) : null}
            {studioMode ? (
              <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: highlightsIntro.eyebrow || highlightsIntro.title || highlightsIntro.subtitle ? 1.2 : 0 }}>
                {studioTarget({ elementId: "highlights.collection", label: "Diferenciales", count: highlights.length })}
                <Chip size="small" label="Editar tarjetas" variant="outlined" />
              </Stack>
            ) : null}
            {visibleHighlights.length ? (
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: responsive(
                    {
                      xs: "minmax(0, 1fr)",
                      sm: visibleHighlights.length === 1 ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                      md: `repeat(${Math.max(1, Math.min(visibleHighlights.length, 4))}, minmax(0, 1fr))`,
                    },
                    "minmax(0, 1fr)"
                  ),
                  gap: responsive({ xs: 1.4, sm: 1.6, md: 2 }, 1.4),
                  mt: highlightsIntro.eyebrow || highlightsIntro.title || highlightsIntro.subtitle ? 2 : 0,
                  alignItems: "stretch",
                }}
              >
                {visibleHighlights.map(({ item, index }) => {
                  const highlightImage = resolveLandingAssetUrl(item.imageUrl);
                  const highlightVideo = youtubeEmbedUrl(item.youtubeUrl);
                  const cardSurface = highlightsAppearance.surfaceColor || highlightsColors.background;
                  return (
                    <Paper
                      key={item.id || index}
                      elevation={0}
                      data-landing-field={studioMode ? `highlights.${index}` : undefined}
                      data-landing-element-type={studioMode ? "collection-item" : undefined}
                      data-landing-edit-label={studioMode ? `Editar ${item.title || `diferencial ${index + 1}`}` : undefined}
                      sx={{
                        minWidth: 0,
                        height: "100%",
                        overflow: "hidden",
                        borderRadius: px(borders.radiusMd, 10),
                        bgcolor: cardSurface,
                        border: cardBorder,
                        boxShadow: shadows.sm,
                        cursor: studioMode ? "pointer" : "default",
                      }}
                    >
                      {highlightVideo ? (
                        <Box
                          component="iframe"
                          src={highlightVideo}
                          title={item.title || `Video del diferencial ${index + 1}`}
                          sx={{ width: "100%", aspectRatio: "16 / 9", display: "block", border: 0, bgcolor: "#000", pointerEvents: studioMode ? "none" : "auto" }}
                        />
                      ) : highlightImage ? (
                        <Box sx={{ position: "relative" }}>
                          <SafeCanvasImage
                            src={highlightImage}
                            alt={item.imageAlt || item.title || ""}
                            sx={{ width: "100%", height: 150 * highlightsImageScale, objectFit: "cover", objectPosition: normalizeImagePosition(item.imagePosition) }}
                          />
                          {item.icon ? (
                            <Box
                              sx={{
                                position: "absolute",
                                left: px(finite(spacing.md, 16), 16),
                                bottom: -20,
                                width: 42,
                                height: 42,
                                borderRadius: px(borders.radiusSm, 5),
                                display: "grid",
                                placeItems: "center",
                                color: highlightsAppearance.accentColor || highlightsColors.primary,
                                bgcolor: cardSurface,
                                border: cardBorder,
                                boxShadow: shadows.sm,
                              }}
                            >
                              <HighlightCanvasIcon name={item.icon} sx={{ fontSize: 24 }} />
                            </Box>
                          ) : null}
                        </Box>
                      ) : item.icon ? (
                        <Box sx={{ px: px(finite(spacing.lg, 24) * highlightsSpacingScale, 24), pt: px(finite(spacing.lg, 24) * highlightsSpacingScale, 24) }}>
                          <Box
                            sx={{
                              width: 46,
                              height: 46,
                              borderRadius: px(borders.radiusSm, 5),
                              display: "grid",
                              placeItems: "center",
                              color: highlightsAppearance.accentColor || highlightsColors.primary,
                              bgcolor: colorWithAlpha(primary, "12"),
                              border: cardBorder,
                            }}
                          >
                            <HighlightCanvasIcon name={item.icon} sx={{ fontSize: 26 }} />
                          </Box>
                        </Box>
                      ) : null}
                      <Box sx={{
                        p: px(finite(spacing.lg, 24) * highlightsSpacingScale, 24),
                        pt: highlightImage && item.icon
                          ? px(finite(spacing.lg, 24) * highlightsSpacingScale + 8, 32)
                          : (highlightImage || highlightVideo)
                            ? px(finite(spacing.lg, 24) * highlightsSpacingScale, 24)
                            : item.icon
                              ? px(finite(spacing.md, 16) * highlightsSpacingScale, 16)
                              : px(finite(spacing.lg, 24) * highlightsSpacingScale, 24),
                      }}>
                        {item.title ? <Typography data-landing-color-field={`highlights.${index}.title`} sx={{ color: highlightsAppearance.titleColor || highlightsColors.secondary, fontWeight: 950 }}>{item.title}</Typography> : null}
                        {item.description ? <Typography data-landing-color-field={`highlights.${index}.description`} variant="body2" sx={{ mt: item.title ? .6 : 0, color: highlightsAppearance.textColor || highlightsColors.mutedText, lineHeight: 1.55 }}>{item.description}</Typography> : null}
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("custom") && (studioMode || custom.eyebrow || custom.title || custom.description || custom.imageUrl || custom.youtubeUrl || custom.calendlyUrl || custom.ctaLabel || custom.ctaUrl) ? (
        <SectionFrame
          sectionId={layoutInstanceId || "custom"}
          sectionType="custom"
          sectionLabel={custom.title || "Sección libre"}
          layoutOrder={orderFor("custom")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("custom")}
          sx={{
            ...segmentCssVariables(theme, "custom"),
            ...elementColorSxForSection("custom"),
            px: horizontalPadding,
            py: responsive({ xs: px(finite(spacing.xl, 32), 32), md: px(finite(spacing.section, 72), 72) }, px(finite(spacing.xl, 32), 32)),
            bgcolor: customColors.background,
          }}
        >
          {(() => {
            const customImage = resolveLandingAssetUrl(custom.imageUrl);
            const customVideo = youtubeEmbedUrl(custom.youtubeUrl);
            const customCalendly = normalizeCalendlyUrl(custom.calendlyUrl);
            const customHasMedia = Boolean(customVideo || customCalendly || customImage);
            const customMediaVisible = customHasMedia || studioMode;
            const customCentered = custom.layout === "centered";
            const customContentLeft = custom.layout === "image-right";
            const customContentRight = !customCentered && !customContentLeft;
            const media = customMediaVisible ? (
              <Stack spacing={1.2} sx={{ width: "100%" }}>
                {customCalendly ? (
                  <Box
                    data-landing-element-id={studioMode ? "custom.reservation" : undefined}
                    data-landing-element-type={studioMode ? "complex" : undefined}
                    data-landing-edit-label={studioMode ? "Editar reserva" : undefined}
                    sx={{ borderRadius: px(borders.radiusMd, 10), overflow: "hidden" }}
                  >
                    <Box
                      component="iframe"
                      src={customCalendly}
                      tabIndex={studioMode ? -1 : undefined}
                      title={custom.title ? `Calendly - ${custom.title}` : "Calendly"}
                      sx={{ width: "100%", height: responsive({ xs: 500, md: 620 }, 500), border: 0, bgcolor: "#fff", pointerEvents: "none", display: "block" }}
                    />
                  </Box>
                ) : studioTarget({ elementId: "custom.reservation", label: "Reserva" })}
                {customVideo ? (
                  <Box
                    data-landing-element-id={studioMode ? "custom.video" : undefined}
                    data-landing-element-type={studioMode ? "complex" : undefined}
                    data-landing-edit-label={studioMode ? "Editar video" : undefined}
                    sx={{ borderRadius: px(borders.radiusMd, 10), overflow: "hidden" }}
                  >
                    <Box component="iframe" src={customVideo} tabIndex={studioMode ? -1 : undefined} title={custom.title || "Video"} sx={{ width: "100%", aspectRatio: "16 / 9", border: 0, bgcolor: "#000", pointerEvents: "none", display: "block" }} />
                  </Box>
                ) : studioTarget({ elementId: "custom.video", label: "Video" })}
                {customImage ? (
                  <Box
                    data-landing-element-id={studioMode ? "custom.image" : undefined}
                    data-landing-element-type={studioMode ? "image" : undefined}
                    data-landing-edit-label={studioMode ? "Editar imagen" : undefined}
                    sx={{ borderRadius: px(borders.radiusMd, 10), overflow: "hidden" }}
                  >
                    <SafeCanvasImage
                      src={customImage}
                      alt={custom.imageAlt || custom.title || ""}
                      sx={{ width: "100%", height: responsive({ xs: 230, md: 330 }, 230), objectFit: "cover", objectPosition: normalizeImagePosition(custom.imagePosition), display: "block" }}
                    />
                  </Box>
                ) : studioTarget({ elementId: "custom.image", label: "Imagen" })}
              </Stack>
            ) : null;
            return (
              <Box sx={{ display: "grid", gridTemplateColumns: customCentered ? "minmax(0,1fr)" : responsive({ xs: "minmax(0,1fr)", md: "minmax(0,1fr) minmax(0,1fr)" }, "minmax(0,1fr)"), gap: responsive({ xs: 2, md: 4 }, 2), alignItems: "center" }}>
                {customMediaVisible && !customCentered && customContentRight ? media : null}
                <Stack
                  spacing={1.1}
                  sx={{
                    gridColumn: customCentered ? "1" : responsive({ xs: "1", md: customContentRight ? "2" : "1" }, "1"),
                    maxWidth: customCentered ? 760 : 620,
                    width: "100%",
                    mx: customCentered ? "auto" : 0,
                    justifySelf: customCentered ? "center" : customContentRight ? "end" : "start",
                    textAlign: customCentered ? "center" : customContentRight ? "right" : "left",
                  }}
                >
                  {custom.eyebrow ? (
                    <Typography data-landing-field="custom.eyebrow" data-landing-element-type="text" data-landing-edit-label="Editar texto superior" variant="overline" sx={{ color: customColors.primary, fontWeight: 900 }}>{custom.eyebrow}</Typography>
                  ) : studioTarget({ fieldPath: "custom.eyebrow", label: "Texto superior", muted: true })}
                  {custom.title ? (
                    <Typography data-landing-field="custom.title" data-landing-element-type="text" data-landing-edit-label="Editar título" sx={{ color: featuredColors.secondary, fontSize: responsive({ xs: 30, md: 38 }, 30), fontWeight: 950 }}>{custom.title}</Typography>
                  ) : studioTarget({ fieldPath: "custom.title", label: "Título" })}
                  {custom.description ? (
                    <Typography data-landing-field="custom.description" data-landing-element-type="text" data-landing-edit-label="Editar descripción" sx={{ color: customColors.mutedText, whiteSpace: "pre-line", lineHeight: 1.65 }}>{custom.description}</Typography>
                  ) : studioTarget({ fieldPath: "custom.description", label: "Descripción", muted: true })}
                  {custom.ctaLabel && (custom.ctaUrl || studioMode) ? (
                    <Button data-landing-field="custom.ctaLabel" data-landing-element-type="button" data-landing-edit-label="Editar botón" variant="contained" sx={{ alignSelf: customCentered ? "center" : customContentRight ? "flex-end" : "flex-start", textTransform: "none" }}>{custom.ctaLabel}</Button>
                  ) : studioTarget({ fieldPath: "custom.ctaLabel", label: "Botón", muted: !custom.ctaUrl })}
                </Stack>
                {customMediaVisible && !customCentered && customContentLeft ? media : null}
                {customMediaVisible && customCentered ? <Box>{media}</Box> : null}
              </Box>
            );
          })()}
        </SectionFrame>
      ) : null}

      {segmentAllowed("contact") && sections.showContact !== false && (contactHasContent || studioMode) ? (
        <SectionFrame
          sectionId="contact"
          layoutOrder={orderFor("contact")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("contact")}
          sx={{
            ...segmentCssVariables(theme, "contact"),
            ...elementColorSxForSection("contact"),
            px: horizontalPadding,
            py: responsive({ xs: px(finite(spacing.xl, 32) * contactSpacingScale, 32), md: px(finite(spacing.section, 72) * contactSpacingScale, 72) }, px(finite(spacing.xl, 32) * contactSpacingScale, 32)),
            color: contactAppearance.textColor || contactColors.onSecondary,
            backgroundColor: contactAppearance.backgroundColor || contactColors.secondary,
          }}
        >
          <Box sx={{ display: "grid", gridTemplateColumns: responsive({ xs: "1fr", md: contactImage || studioMode ? "1.35fr .65fr" : "1fr" }, "1fr"), gap: responsive({ xs: 2, md: 3 }, 2), alignItems: "stretch" }}>
            <Box sx={{ minWidth: 0, alignSelf: "center" }}>
              {contact.title ? (
                <Typography data-landing-field="contact.title" data-landing-edit-label="Editar título de contacto" sx={{ color: contactAppearance.titleColor || "inherit", fontSize: responsive({ xs: 30 * contactTitleScale, md: 34 * contactTitleScale }, 30 * contactTitleScale), fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{contact.title}</Typography>
              ) : studioTarget({ fieldPath: "contact.title", label: "Título", muted: true })}
              {contact.subtitle ? (
                <Typography data-landing-field="contact.subtitle" data-landing-edit-label="Editar texto de apoyo" sx={{ mt: contact.title ? .8 : 0, opacity: .78 }}>{contact.subtitle}</Typography>
              ) : studioTarget({ fieldPath: "contact.subtitle", label: "Texto de apoyo", muted: true })}

              <Stack direction={responsive({ xs: "column", sm: "row" }, "column")} spacing={1.2} useFlexGap flexWrap="wrap" sx={{ mt: contact.title || contact.subtitle ? 2 : 0 }}>
                {whatsappDigits && contact.whatsappCtaLabel ? (
                  <Button
                    data-landing-field="contact.whatsappCtaLabel"
                    data-landing-element-type="button"
                    data-landing-edit-label="Editar botón de WhatsApp"
                    variant="contained"
                    startIcon={<WhatsAppIcon />}
                    sx={{ bgcolor: contactAppearance.accentColor || contactColors.primary, color: contactColors.onPrimary, textTransform: "none", boxShadow: shadows.sm }}
                  >
                    {contact.whatsappCtaLabel}
                  </Button>
                ) : studioTarget({ fieldPath: "contact.whatsappCtaLabel", label: "Botón de WhatsApp", muted: true })}
                {contact.detailsCtaLabel && contact.detailsCtaUrl ? (
                  <Button
                    data-landing-field="contact.detailsCtaLabel"
                    data-landing-element-type="button"
                    data-landing-edit-label="Editar botón de detalles"
                    variant="outlined"
                    sx={{ color: contactAppearance.titleColor || contactColors.onSecondary, borderColor: "rgba(255,255,255,.7)", textTransform: "none" }}
                  >
                    {contact.detailsCtaLabel}
                  </Button>
                ) : studioTarget({ fieldPath: "contact.detailsCtaLabel", label: "Botón de detalles", muted: true })}
              </Stack>

              {contactHasDetails ? (
                <Stack
                  direction={responsive({ xs: "column", sm: "row" }, "column")}
                  spacing={0.9}
                  useFlexGap
                  flexWrap="wrap"
                  data-landing-element-id="contact.business-data"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar datos del negocio"
                  sx={{ mt: 1.2, p: studioMode ? 0.7 : 0, borderRadius: 2 }}
                >
                  {businessAddress ? <Chip label={businessAddress} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff", "& .MuiChip-label": { overflowWrap: "anywhere" } }} /> : null}
                  {business.schedule ? <Chip label={business.schedule} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff", "& .MuiChip-label": { overflowWrap: "anywhere" } }} /> : null}
                  {business.email ? <Chip label={business.email} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff", "& .MuiChip-label": { overflowWrap: "anywhere" } }} /> : null}
                  {business.phone ? <Chip label={business.phone} sx={{ maxWidth: "100%", bgcolor: "rgba(255,255,255,.1)", color: "#fff" }} /> : null}
                </Stack>
              ) : studioMode ? (
                <Stack sx={{ mt: 1.2 }}>
                  {studioTarget({ elementId: "contact.business-data", label: "Datos del negocio", count: 0, muted: true })}
                </Stack>
              ) : null}

              {studioMode ? (
                <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" sx={{ mt: 1.1 }}>
                  {studioTarget({ elementId: "contact.socials", label: "Redes sociales", count: contactSocialCount, muted: contactSocialCount === 0 })}
                  <Chip size="small" label="Datos compartidos" variant="outlined" sx={{ color: "inherit", borderColor: "currentColor", opacity: .72 }} />
                </Stack>
              ) : null}
            </Box>

            {contactImage ? (
              <SafeCanvasImage
                src={contactImage}
                alt={contact.mapTitle || ""}
                landingElementId={studioMode ? "contact.map" : ""}
                landingElementType="complex"
                editLabel="Editar mapa e imagen"
                sx={{ width: "100%", minHeight: responsive({ xs: 220 * contactImageScale, md: 300 * contactImageScale }, 220 * contactImageScale), maxHeight: 430 * contactImageScale, height: "100%", objectFit: "cover", objectPosition: contactImagePosition, borderRadius: px(borders.radiusLg, 18), border: "1px solid rgba(255,255,255,.18)", boxShadow: shadows.md }}
              />
            ) : studioMode ? (
              <Box
                data-landing-element-id="contact.map"
                data-landing-element-type="complex"
                data-landing-edit-label="Editar mapa e imagen"
                sx={{
                  minHeight: responsive({ xs: 170, md: 260 }, 170),
                  display: "grid",
                  placeItems: "center",
                  p: 2,
                  border: "1px dashed rgba(255,255,255,.5)",
                  borderRadius: px(borders.radiusLg, 18),
                  bgcolor: "rgba(255,255,255,.06)",
                  textAlign: "center",
                }}
              >
                <Stack spacing={0.8} alignItems="center">
                  <Typography sx={{ fontWeight: 900 }}>Mapa e imagen</Typography>
                  <Typography variant="caption" sx={{ opacity: .72 }}>Tocá para configurar la imagen, el encuadre y el mapa.</Typography>
                  {business.mapEmbedUrl ? <Chip size="small" label="Mapa conectado" /> : <Chip size="small" label="Sin configurar" variant="outlined" sx={{ color: "inherit", borderColor: "currentColor" }} />}
                </Stack>
              </Box>
            ) : null}
          </Box>
        </SectionFrame>
      ) : null}

      {segmentAllowed("footer") && sections.showFooter !== false && (footerHasVisibleContent || studioMode) ? (
        <SectionFrame
          sectionId="footer"
          layoutOrder={orderFor("footer")}
          activeSection={activeSection}
          studioMode={studioMode}
          onSelectSection={onSelectSection}
          onSelectElement={onSelectElement}
          selectedElement={selectedElement}
          onOpenSegmentTool={onOpenSegmentTool}
          onToggleSection={onToggleSection}
          device={previewDevice.id}
          backgroundSx={sectionBackgroundSx("footer")}
          sx={{
            ...segmentCssVariables(theme, "footer"),
            ...elementColorSxForSection("footer"),
            px: horizontalPadding,
            py: px(finite(spacing.lg, 24) * footerSpacingScale, 24),
            bgcolor: footerStyle === "style-2" ? footerColors.surface : footerStyle === "style-3" ? "#10171e" : footerColors.secondary,
            color: footerStyle === "style-2" ? footerColors.text : footerColors.onSecondary,
            borderTop: footerStyle === "style-2" ? cardBorder : "none",
          }}
        >
          <Stack spacing={2} alignItems={footerStyle === "style-3" ? "center" : "stretch"} textAlign={footerStyle === "style-3" ? "center" : "left"}>
            {studioMode ? (
              <Stack direction="row" spacing={0.65} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                {footer.showLogo === false || !logo ? studioTarget({ elementId: "footer.logo", label: "Logo", muted: true }) : null}
                {!footerHasBrand ? studioTarget({ elementId: "footer.brand", label: "Marca", muted: true }) : null}
                {!footerHasNavigation ? studioTarget({ elementId: "footer.navigation", label: "Navegación", muted: true }) : null}
                {!footerHasContact ? studioTarget({ elementId: "footer.contact", label: "Contacto", muted: true }) : null}
                {navigation.showSocialLinks === false || !footerSocialLabels.length ? studioTarget({ elementId: "footer.socials", label: "Redes", muted: true }) : null}
                {!footerCopyright ? studioTarget({ fieldPath: "footer.copyright", label: "Copyright", muted: true }) : null}
              </Stack>
            ) : null}

            <Stack direction={footerStyle === "style-3" ? "column" : responsive({ xs: "column", md: "row" }, "column")} justifyContent="space-between" alignItems={footerStyle === "style-3" ? "center" : "flex-start"} spacing={3} sx={{ width: "100%" }}>
              {footerHasBrand ? (
                <Stack
                  direction={footerStyle === "style-3" ? "column" : "row"}
                  alignItems={footerStyle === "style-3" ? "center" : "flex-start"}
                  spacing={1.1}
                  data-landing-element-id="footer.brand"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar marca del pie de página"
                  sx={{ minWidth: 0, flex: "1 1 260px" }}
                >
                  {footer.showLogo !== false && logo ? (
                    <Box
                      data-landing-element-id="footer.logo"
                      data-landing-element-type="image"
                      data-landing-edit-label="Editar logo del pie de página"
                      sx={{ display: "inline-flex", flexShrink: 0 }}
                    >
                      <SafeCanvasImage
                        src={logo}
                        alt=""
                        monochromeColor={footerLogoColor}
                        sx={footerLogoMaxHeight !== null
                          ? {
                              display: "block",
                              width: "auto",
                              maxWidth: "100%",
                              height: footerLogoMaxHeight,
                              maxHeight: footerLogoMaxHeight,
                              objectFit: "contain",
                              objectPosition: footerLogoPosition,
                              flexShrink: 0,
                            }
                          : { width: 42 * footerImageScale, height: 42 * footerImageScale, objectFit: "contain", objectPosition: footerLogoPosition, flexShrink: 0 }}
                      />
                    </Box>
                  ) : null}
                  {business.name || footerBrandTagline ? <Box sx={{ minWidth: 0 }}>
                    {business.name ? <Typography noWrap sx={{ fontSize: `${finite(typography.baseFontSize, 16) * footerTitleScale}px`, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)" }}>{business.name}</Typography> : null}
                    {footerBrandTagline ? <Typography variant="caption" sx={{ display: "block", mt: business.name ? 0.35 : 0, opacity: .72, whiteSpace: "pre-line" }}>{footerBrandTagline}</Typography> : null}
                  </Box> : null}
                </Stack>
              ) : null}

              {footerHasNavigation ? (
                <Box
                  data-landing-element-id="footer.navigation"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar navegación del pie de página"
                  sx={{ flex: "1 1 280px", minWidth: 0 }}
                >
                  {footer.navigationTitle ? <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.navigationTitle}</Typography> : null}
                  <Stack direction="row" spacing={1.4} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                    {footerHomeLabel ? <Typography variant="caption">{footerHomeLabel}</Typography> : null}
                    {productsEnabled && navigation.showProductsLink !== false && footerProductsLabel ? <Typography variant="caption">{footerProductsLabel}</Typography> : null}
                    {footerAboutVisible && navigation.aboutLabel ? <Typography variant="caption">{navigation.aboutLabel}</Typography> : null}
                    {footerHighlightsVisible && navigation.highlightsLabel ? <Typography variant="caption">{navigation.highlightsLabel}</Typography> : null}
                    {footerContactVisible && footerContactLabel ? <Typography variant="caption">{footerContactLabel}</Typography> : null}
                  </Stack>
                </Box>
              ) : null}

              {footerHasContact ? (
                <Box
                  data-landing-element-id="footer.contact"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar contacto del pie de página"
                  sx={{ flex: "0 1 220px", minWidth: 0 }}
                >
                  {footer.contactTitle ? <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{footer.contactTitle}</Typography> : null}
                  {businessAddress ? <Typography variant="caption" sx={{ display: "block", opacity: .72, overflowWrap: "anywhere" }}>{businessAddress}</Typography> : null}
                  {business.schedule ? <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72, overflowWrap: "anywhere" }}>{business.schedule}</Typography> : null}
                  {business.email ? <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72, overflowWrap: "anywhere" }}>{business.email}</Typography> : null}
                  {business.phone ? <Typography variant="caption" sx={{ display: "block", mt: 0.25, opacity: .72 }}>{business.phone}</Typography> : null}
                </Box>
              ) : null}

              {footerSocialLabels.length ? (
                <Box
                  data-landing-element-id="footer.socials"
                  data-landing-element-type="complex"
                  data-landing-edit-label="Editar redes sociales del pie de página"
                  sx={{ flex: "0 1 220px", minWidth: 0 }}
                >
                  <Typography variant="caption" sx={{ display: "block", mb: 0.7, fontWeight: 900 }}>{navigation.socialTitle || "Redes sociales"}</Typography>
                  <Stack direction="row" spacing={0.8} useFlexGap flexWrap="wrap" justifyContent={footerStyle === "style-3" ? "center" : "flex-start"}>
                    {footerSocialLabels.map((label) => <Chip key={label} size="small" label={label} sx={{ maxWidth: "100%", bgcolor: footerStyle === "style-2" ? background : "rgba(255,255,255,.1)", color: "inherit" }} />)}
                  </Stack>
                </Box>
              ) : null}
            </Stack>

            {footerCopyright ? (
              <Typography data-landing-field="footer.copyright" data-landing-element-type="text" data-landing-edit-label="Editar copyright" variant="caption" sx={{ width: "100%", pt: 1.2, borderTop: footerStyle === "style-2" ? `1px solid ${footerColors.divider}` : "1px solid rgba(255,255,255,.12)", opacity: .65 }}>
                {footerCopyright}
              </Typography>
            ) : null}
          </Stack>
        </SectionFrame>
      ) : null}

      {!bare && !onlySection ? layoutSegments
        .filter((segment) => segment.id !== segment.type && segment.visible !== false)
        .map((segment) => (
          <LandingCanvas
            key={segment.id}
            form={applyLandingSegmentSnapshot(form, segment)}
            activeSection={studioMode && segment.type === "custom" ? activeSection : ""}
            selectedElement={studioMode && segment.type === "custom" ? selectedElement : null}
            device={device}
            productsEnabled={productsEnabled}
            studioMode={studioMode && segment.type === "custom"}
            onSelectSection={segment.type === "custom" ? onSelectSection : undefined}
            onSelectElement={segment.type === "custom" ? onSelectElement : undefined}
            onOpenSegmentTool={segment.type === "custom" ? onOpenSegmentTool : undefined}
            onToggleSection={segment.type === "custom" ? onToggleSection : undefined}
            onlySection={segment.type}
            bare
            layoutInstanceId={segment.id}
          />
        )) : null}

      {!bare && !onlySection && (studioMode || (business.whatsapp && sections.showFloatingWhatsapp !== false)) ? (
        <Box
          role="button"
          tabIndex={0}
          data-landing-element-id={studioMode ? "floatingWhatsapp.button" : undefined}
          data-landing-element-type={studioMode ? "complex" : undefined}
          data-landing-edit-label={studioMode ? "Editar WhatsApp flotante" : undefined}
          data-landing-element-selected={studioMode && selectedElement?.sectionId === "floatingWhatsapp" && selectedElement?.elementId === "floatingWhatsapp.button" ? "true" : undefined}
          aria-label={studioMode
            ? `Editar WhatsApp flotante. ${business.whatsapp ? "WhatsApp configurado" : "Falta número"}${sections.showFloatingWhatsapp === false ? ". Oculto" : ""}`
            : (contact.floatingWhatsappAriaLabel || "Contactar por WhatsApp")}
          title={studioMode ? "Editar WhatsApp flotante" : undefined}
          onClick={(event) => {
            if (!studioMode) return;
            event.preventDefault();
            event.stopPropagation();
            onSelectElement?.({
              sectionId: "floatingWhatsapp",
              sectionType: "floatingWhatsapp",
              fieldPath: "",
              elementId: "floatingWhatsapp.button",
              logicalType: "complex",
              previewDevice: device || "desktop",
            });
          }}
          onKeyDown={(event) => {
            if (!studioMode || (event.key !== "Enter" && event.key !== " ")) return;
            event.preventDefault();
            event.stopPropagation();
            onSelectElement?.({
              sectionId: "floatingWhatsapp",
              sectionType: "floatingWhatsapp",
              fieldPath: "",
              elementId: "floatingWhatsapp.button",
              logicalType: "complex",
              previewDevice: device || "desktop",
            });
          }}
          sx={{
            position: "absolute",
            right: 18,
            bottom: 18,
            width: 52,
            height: 52,
            borderRadius: "50%",
            display: "grid",
            placeItems: "center",
            bgcolor: "#25D366",
            color: "#fff",
            cursor: studioMode ? "pointer" : "default",
            opacity: studioMode && (sections.showFloatingWhatsapp === false || !business.whatsapp) ? 0.52 : 1,
            outline: studioMode && selectedElement?.sectionId === "floatingWhatsapp" && selectedElement?.elementId === "floatingWhatsapp.button"
              ? "3px solid rgba(243,90,58,.96)"
              : studioMode && (sections.showFloatingWhatsapp === false || !business.whatsapp)
                ? "2px dashed rgba(25,36,47,.42)"
                : "2px solid transparent",
            outlineOffset: 3,
            boxShadow: activeSection === "floatingWhatsapp" ? "0 0 0 4px rgba(243,90,58,.24), 0 8px 24px rgba(0,0,0,.25)" : "0 8px 24px rgba(0,0,0,.25)",
            transition: "opacity .15s ease, outline-color .15s ease, box-shadow .15s ease",
            "&:hover": studioMode ? { opacity: 1, outlineColor: "rgba(243,90,58,.86)" } : undefined,
            "&:focus-visible": studioMode ? { opacity: 1, outlineColor: "rgba(243,90,58,1)" } : undefined,
            zIndex: 4,
          }}
        >
          <WhatsAppIcon />
        </Box>
      ) : null}
    </Box>
  );
}
